import { useEffect, useMemo, useRef, useState } from "react";
import type { Alert, AlertLink, AlertSeverity } from "@chula/shared";
import { ALERT_SEVERITY_RANK } from "@chula/shared";
import { TIER_TAG } from "../lib/tierDisplay";
import { PanelHeader } from "./PanelHeader";
import { summariseAlerts, type AlertSummary, type UseAlertsResult } from "../hooks/useAlerts";

export { summariseAlerts };
export type { AlertSummary };
import "./AttentionQueue.css";

const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  critical: "CRIT",
  warning: "WARN",
  watch: "WATCH",
  info: "INFO",
};

type Mode = "assign" | "resolve" | "note";

interface AttentionQueueProps {
  /** The result of useAlerts() — App calls the hook once and shares it with AttentionSummary. */
  queue: UseAlertsResult;
  /** Fly the map to an alert's location ([lng, lat]). */
  onFocus?: (location: [number, number], alert: Alert) => void;
  /** Open the linked issue card (citizen-report SLA alerts carry one). */
  onOpenIncident?: (link: AlertLink, alert: Alert) => void;
  /** Collapse to this many rows (default: all). */
  maxRows?: number;
  /** Connector id → the connector's own display label, so no key reaches the screen. */
  sourceNames?: ReadonlyMap<string, string>;
}

/** Needs-attention order: critical first, real before simulated, oldest first. */
export function rankForAttention(alerts: Alert[]): Alert[] {
  return alerts
    .filter((a) => a.status !== "resolved")
    .slice()
    .sort((a, b) =>
      ALERT_SEVERITY_RANK[b.severity] - ALERT_SEVERITY_RANK[a.severity]
      || Number(a.simulated) - Number(b.simulated)
      || a.openedAt.localeCompare(b.openedAt));
}

export function fmtSince(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const min = Math.floor((now - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(min)) return "—";
  if (min < 1) return "<1m";
  if (min < 60) return `${min}m`;
  if (min < 48 * 60) return `${Math.floor(min / 60)}h`;
  return `${Math.floor(min / 1440)}d`;
}

function fmtClock(iso: string | null | undefined): string {
  if (!iso) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "never" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * Plain-language name for an alert's source reference (no internal keys on screen).
 *
 * `names` carries the connector registry's own display labels (id → label), so a
 * connector reads as "Campus meters (pushed)" and never as "campus-meters-demo".
 * Without the registry the id is humanised rather than printed raw.
 */
export function friendlySource(ref: string | null | undefined, names?: ReadonlyMap<string, string>): string {
  if (!ref) return "";
  const [kind, id = ""] = ref.split(":");
  const KNOWN: Record<string, string> = {
    "campus-reports": "Citizen reports",
    "air-quality": "Air quality",
    "flood-campus-stations": "Flood gauges",
    "itic": "Traffic incidents",
    "city-reports": "Citizen reports",
    "weather": "Weather",
    "fire-hose": "Fire hose (modeled)",
  };
  if (KNOWN[kind]) return KNOWN[kind];
  const base = id.replace(/:v\d+$/, "");
  if (KNOWN[base]) return KNOWN[base];
  if (kind === "connector") {
    const named = names?.get(base);
    if (named) return named;
    return `Source: ${base.replace(/-/g, " ")}`;
  }
  if (kind === "file") return base.split("/").pop()?.replace(/\.geojson$/, "").replace(/-/g, " ") ?? base;
  return base.replace(/-/g, " ");
}

/**
 * True when the row's title already names this source.
 *
 * The title is the row's headline; the foot repeats the source. When the
 * title already says it — "Feed stale: Citizen reports feed" for the Citizen
 * reports feed — the second copy is the same claim twice, not more
 * information. In a 273px rail it is also a wrapped line: the four rows that
 * repeated their source cost ~18px each and pushed the fifth row off the
 * first screen (measured 2026-10-23: 54px of overflow at 1440×900).
 *
 * The same rule already governs the message (a feed-stale alert's message
 * does not repeat the title's label). This is the foot's turn.
 */
export function titleNamesSource(title: string, source: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const t = norm(title);
  const s = norm(source);
  if (!t || !s) return false;
  if (t.includes(s)) return true;
  // "Fire hose (modeled)" → "fire hose": the qualifier is already carried by
  // the SIM badge, so the subject alone is enough to count as named. The
  // qualifier is stripped from the raw string — normalising first would have
  // deleted the parentheses along with it.
  const head = norm(source.replace(/\s*\([^)]*\)\s*$/, ""));
  return head.length >= 4 && t.includes(head);
}

export type AttentionItem =
  | { kind: "single"; alert: Alert }
  | { kind: "group"; ruleId: string; alerts: Alert[] };

/**
 * Collapse alerts raised by the same rule into one line ("10 · Citizen report
 * open over 3 days"). Ten identical cards bury everything else; an executive
 * needs the count first and the list on demand. Order follows the ranking.
 */
export function groupForAttention(ranked: Alert[]): AttentionItem[] {
  const byRule = new Map<string, Alert[]>();
  for (const a of ranked) {
    const list = byRule.get(a.ruleId);
    if (list) list.push(a); else byRule.set(a.ruleId, [a]);
  }
  const out: AttentionItem[] = [];
  const emitted = new Set<string>();
  for (const a of ranked) {
    if (emitted.has(a.ruleId)) continue;
    emitted.add(a.ruleId);
    const list = byRule.get(a.ruleId)!;
    out.push(list.length > 1 ? { kind: "group", ruleId: a.ruleId, alerts: list } : { kind: "single", alert: a });
  }
  return out;
}

interface RowProps {
  alert: Alert;
  queue: UseAlertsResult;
  onFocus?: AttentionQueueProps["onFocus"];
  onOpenIncident?: AttentionQueueProps["onOpenIncident"];
  sourceNames?: AttentionQueueProps["sourceNames"];
}

function AlertRow({ alert, queue, onFocus, onOpenIncident, sourceNames }: RowProps) {
  const [mode, setMode] = useState<Mode | null>(null);
  const [assignee, setAssignee] = useState(alert.assignee ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [returnTo, setReturnTo] = useState<Mode | null>(null);
  const [showActions, setShowActions] = useState(false);
  const firstField = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const buttons = useRef<Partial<Record<Mode, HTMLButtonElement | null>>>({});

  useEffect(() => {
    if (mode) firstField.current?.focus();
    else if (returnTo) {
      // The action buttons re-mount when the form closes: focus the new one.
      buttons.current[returnTo]?.focus();
      setReturnTo(null);
    }
  }, [mode, returnTo]);

  const open = (m: Mode) => {
    setNote("");
    setMode(m);
  };
  const close = () => {
    setReturnTo(mode);
    setMode(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mode || (mode === "resolve" && !note.trim())) return;
    setBusy(true);
    const ok = mode === "assign"
      ? await queue.assign(alert.id, assignee, note)
      : mode === "resolve"
        ? await queue.resolve(alert.id, note)
        : await queue.addNote(alert.id, note);
    setBusy(false);
    if (ok) close();
  };

  const tl = TIER_TAG[alert.sourceTier] ?? "";
  const canSubmit = !busy && (mode !== "assign" || assignee.trim() !== "") && ((mode !== "note" && mode !== "resolve") || note.trim() !== "");
  const focusable = alert.location && onFocus;

  // Severity, title and message read as ONE block of prose: the chip sits in
  // the title's text flow so it shares the first line instead of claiming a
  // line of its own, and the title keeps the full column width to wrap in.
  const summary = (
    <>
      <span className={`aq-sev mono aq-sev--${alert.severity}`}>{SEVERITY_LABEL[alert.severity]}</span>
      <span className="aq-title">{alert.title}</span>
      {alert.simulated && (
        <span className="aq-badge aq-badge--sim mono" title="Simulated: raised from modeled (scenario) data, not a measurement">SIM</span>
      )}
      {!alert.simulated && tl && (
        <span className="aq-badge mono" title={`Source tier: ${alert.sourceTier}`}>{tl}</span>
      )}
      {alert.conditionCleared && (
        <span className="aq-badge aq-badge--cleared mono" title="The condition has cleared; a critical alert stays open until someone resolves it">CLEARED</span>
      )}
      <span className="aq-msg">{alert.message}</span>
    </>
  );

  return (
    <li className={`aq-row aq-row--${alert.severity}${alert.simulated ? " aq-row--sim" : ""}`}>
      {focusable ? (
        <button
          type="button"
          className="aq-main"
          onClick={() => alert.location && onFocus?.(alert.location, alert)}
          title="Show on map"
        >
          {summary}
        </button>
      ) : (
        <div className="aq-main">{summary}</div>
      )}

      {/* The facts band carries the row's own action. It used to own a line of
          its own under the message, which pushed every row's real content down
          the rail and left the button stranded on a line by itself. */}
      <div className="aq-foot">
        <span className="aq-meta mono">
          <span title={`Raised ${alert.openedAt}`}>raised {fmtSince(alert.openedAt)} ago</span>
          <span>{alert.assignee ? `→ ${alert.assignee}` : "unowned"}</span>
          {alert.status === "acknowledged" && <span className="aq-ack">ACK</span>}
          {alert.reopenCount > 0 && <span title="Times this condition came back after being resolved">↺{alert.reopenCount}</span>}
          {!titleNamesSource(alert.title, friendlySource(alert.sourceRef, sourceNames)) && (
            <span className="aq-src" title={alert.sourceRef}>{friendlySource(alert.sourceRef, sourceNames)}</span>
          )}
        </span>
        {!mode && (
          <button
            type="button"
            className="aq-disclose mono"
            aria-label="Act on this"
            aria-expanded={showActions}
            onClick={() => setShowActions((v) => !v)}
          >
            {showActions ? "Hide actions" : "Act"}
          </button>
        )}
      </div>

      {!mode && showActions && (
        <div className="aq-actions" role="group" aria-label={`Actions for ${alert.title}`}>
          {alert.status === "open" && (
            <button type="button" className="aq-btn" disabled={busy} onClick={async () => { setBusy(true); await queue.acknowledge(alert.id); setBusy(false); }}>
              Ack
            </button>
          )}
          <button type="button" className="aq-btn" ref={(el) => { buttons.current.assign = el; }} onClick={() => open("assign")}>Assign</button>
          <button type="button" className="aq-btn" ref={(el) => { buttons.current.resolve = el; }} onClick={() => open("resolve")}>Resolve</button>
          <button type="button" className="aq-btn aq-btn--quiet" ref={(el) => { buttons.current.note = el; }} onClick={() => open("note")}>Note</button>
          {alert.link && onOpenIncident && (
            <button type="button" className="aq-btn aq-btn--quiet" onClick={() => onOpenIncident(alert.link!, alert)}>
              Open {alert.link.ticket ?? "report"}
            </button>
          )}
        </div>
      )}

      {mode && (
        <form
          className="aq-form"
          onSubmit={submit}
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}
          aria-label={`${mode} ${alert.title}`}
        >
          {mode === "assign" && (
            <label className="aq-field">
              <span>Owner</span>
              <input
                ref={(el) => { firstField.current = el; }}
                value={assignee}
                maxLength={64}
                onChange={(e) => setAssignee(e.target.value)}
                placeholder="Team or person"
              />
            </label>
          )}
          <label className="aq-field">
            <span>{mode === "resolve" ? "Resolution evidence" : mode === "note" ? "Note" : "Note (optional)"}</span>
            <textarea
              ref={(el) => { if (mode !== "assign") firstField.current = el; }}
              value={note}
              required={mode === "resolve" || mode === "note"}
              placeholder={mode === "resolve" ? "What was checked or fixed? Include a work order or evidence reference." : undefined}
              maxLength={1000}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <div className="aq-actions">
            <button type="submit" className="aq-btn aq-btn--primary" disabled={!canSubmit}>
              {mode === "assign" ? "Assign" : mode === "resolve" ? "Resolve" : "Add note"}
            </button>
            <button type="button" className="aq-btn aq-btn--quiet" onClick={close}>Cancel</button>
          </div>
        </form>
      )}
    </li>
  );
}

function AlertGroupRow({ alerts, queue, onFocus, onOpenIncident, sourceNames }: { alerts: Alert[] } & Omit<RowProps, "alert">) {
  const [open, setOpen] = useState(false);
  const head = alerts[0];
  const unowned = alerts.filter((a) => !a.assignee).length;
  const acked = alerts.filter((a) => a.status === "acknowledged").length;
  const oldest = alerts.reduce((o, a) => (a.openedAt < o ? a.openedAt : o), head.openedAt);
  return (
    <li className={`aq-row aq-row--group aq-row--${head.severity}${head.simulated ? " aq-row--sim" : ""}`}>
      <button type="button" className="aq-main" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="aq-line1">
          <span className={`aq-sev mono aq-sev--${head.severity}`}>{SEVERITY_LABEL[head.severity]}</span>
          <span className="aq-count mono">{alerts.length}</span>
          <span className="aq-chevron" aria-hidden>{open ? "▾" : "▸"}</span>
          <span className="aq-title">{head.title}</span>
          {head.simulated && <span className="aq-badge aq-badge--sim mono" title="Simulated: raised from modeled (scenario) data">SIM</span>}
        </span>
        <span className="aq-meta mono">
          <span>{unowned === alerts.length ? "none owned" : `${alerts.length - unowned} owned · ${unowned} unowned`}</span>
          {acked > 0 && <span className="aq-ack">{acked} ACK</span>}
          <span title={`First raised ${oldest} (the report's own age is in each row's message)`}>raised {fmtSince(oldest)} ago</span>
          {!titleNamesSource(head.title, friendlySource(head.sourceRef, sourceNames)) && (
            <span className="aq-src" title={head.sourceRef}>{friendlySource(head.sourceRef, sourceNames)}</span>
          )}
        </span>
      </button>
      {open && (
        <ul className="aq-list aq-list--nested">
          {alerts.map((a) => (
            <AlertRow key={a.id} alert={a} queue={queue} onFocus={onFocus} onOpenIncident={onOpenIncident} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Announces newly arrived critical alerts once, politely. */
function useCriticalAnnouncement(alerts: Alert[]): string {
  const seen = useRef<Set<string> | null>(null);
  const [text, setText] = useState("");
  useEffect(() => {
    const crit = alerts.filter((a) => a.severity === "critical" && a.status === "open");
    if (seen.current === null) {
      // First load: remember, don't announce the backlog.
      seen.current = new Set(crit.map((a) => a.id));
      return;
    }
    const fresh = crit.filter((a) => !seen.current!.has(a.id));
    for (const a of fresh) seen.current.add(a.id);
    if (fresh.length === 1) setText(`New critical alert: ${fresh[0].title}. ${fresh[0].message}`);
    else if (fresh.length > 1) setText(`${fresh.length} new critical alerts.`);
  }, [alerts]);
  return text;
}

export function AttentionQueue({ queue, onFocus, onOpenIncident, maxRows, sourceNames }: AttentionQueueProps) {
  const ranked = useMemo(() => rankForAttention(queue.alerts), [queue.alerts]);
  const items = useMemo(() => groupForAttention(ranked), [ranked]);
  const shown = maxRows ? items.slice(0, maxRows) : items;
  const announcement = useCriticalAnnouncement(queue.alerts);
  const offline = queue.tier === "unavailable" || queue.engine?.running === false;

  return (
    <section className="aq" aria-label="Needs attention">
      <PanelHeader
        title="Needs attention"
        fallbackTier={queue.tier === "loading" ? "database" : queue.tier}
        source="alert-engine"
      />
      <div className="aq-live" aria-live="polite" role="status">{announcement}</div>
      {queue.actionError && <p className="aq-error" role="alert">{queue.actionError}</p>}

      {queue.tier === "loading" && <p className="aq-empty">Loading alerts…</p>}

      {queue.tier !== "loading" && offline && (
        <p className="aq-empty aq-empty--offline">
          Alert engine offline{queue.error ? ` (${queue.error})` : ""} — the queue below may be out of date.
        </p>
      )}

      {queue.tier !== "loading" && !offline && ranked.length === 0 && (
        <p className="aq-empty">
          Nothing needs attention.
          <span className="aq-empty-sub mono">
            Last checked {fmtClock(queue.engine?.lastEvaluatedAt)} · {queue.engine?.rules ?? 0} rules
            {queue.engine?.ruleErrors ? ` · ${queue.engine.ruleErrors} invalid` : ""}
          </span>
        </p>
      )}

      {shown.length > 0 && (
        <ul className="aq-list">
          {shown.map((it) => it.kind === "group"
            ? <AlertGroupRow key={`g:${it.ruleId}`} alerts={it.alerts} queue={queue} onFocus={onFocus} onOpenIncident={onOpenIncident} sourceNames={sourceNames} />
            : <AlertRow key={it.alert.id} alert={it.alert} queue={queue} onFocus={onFocus} onOpenIncident={onOpenIncident} sourceNames={sourceNames} />)}
        </ul>
      )}
      {maxRows && items.length > maxRows && (
        <p className="aq-more mono">+{items.length - maxRows} more groups</p>
      )}
    </section>
  );
}

interface AttentionSummaryProps {
  summary: AlertSummary;
  /** Click → open the queue. */
  onOpen?: () => void;
}

/** Compact counts by severity for a status bar. */
export function AttentionSummary({ summary, onOpen }: AttentionSummaryProps) {
  const { total, bySeverity: n } = summary;
  const label = total === 0
    ? "All clear: no open alerts"
    : `${total} open alerts: ${n.critical} critical, ${n.warning} warning, ${n.watch + n.info} watch`;
  const body = total === 0 ? (
    <span className="aqs-clear mono">ALL CLEAR</span>
  ) : (
    <>
      {n.critical > 0 && <span className="aqs-n aqs-n--critical mono">{n.critical} CRIT</span>}
      {n.warning > 0 && <span className="aqs-n aqs-n--warning mono">{n.warning} WARN</span>}
      {n.watch + n.info > 0 && <span className="aqs-n aqs-n--watch mono">{n.watch + n.info} WATCH</span>}
    </>
  );
  return onOpen ? (
    <button type="button" className="aqs" onClick={onOpen} aria-label={label} title={label}>{body}</button>
  ) : (
    <span className="aqs" role="status" aria-label={label} title={label}>{body}</span>
  );
}
